import type {
  BillingAccount,
  BillingCatalogue,
  BillingGroupKey,
  BillingLimits,
} from 'src/app/pages/organizations/organization/settings/billing/data/billing.model';

/**
 * Create-form price summary sample layer — answers the three billing reads the create forms' summary makes
 * (`/billing/catalogue`, `/billing/{org}/account`, `/billing/{org}/limits`) while `backend/api` does not have them,
 * with the design's price list and its two example organizations (`wovenword/Create-Form Summaries/index.html`,
 * `config.js` + `engine.js`).
 *
 * It only ever runs when the price summary is switched on (`PRICE_SUMMARY_ENABLED` or `?prices=on`): switched
 * off, the summary makes no billing requests. Like Plans & billing's layer it does not follow
 * `INVENTED_DATA_ENABLED` — the prices are not shown to anyone until the switch is on, and every answer is
 * labelled "Sample data" by the summary (Ansar, 13 Oct 2026).
 *
 * The create forms get the design's prices (subtitles $0.10 and $0.65 a minute, Ansar 13 Oct), so this list
 * deliberately differs from Plans & billing's sample list; the real catalogue will be one list for both.
 *
 * Sample switches on the create form's address: `?sampleOrg=onehope` (Enterprise, 40% off; default Lighthouse
 * Media at list prices) · `?sample=out` (not enough credit) · `?sampleRole=member` (the viewer is a member, so
 * the approval limit applies).
 *
 * **To remove it:** delete the three `price-summary-demo-backend*` files and the word
 * `priceSummaryDemoBackendInterceptor` from `withInterceptors([...])` in `app.config.ts`.
 */

type Unit = 'min' | 'word' | 'film min' | 'change';

/** The design's price list (Per task pricing v3, as the create forms apply it since 26 Sep 2026). */
const RATES: [key: string, label: string, price: number, unit: Unit, group: BillingGroupKey][] = [
  ['STT', 'Transcription', 0.015, 'min', 'subs'],
  ['SRT', 'Subtitles (.srt)', 0.10, 'min', 'subs'],
  ['SUBAI', 'AI subtitle refinement', 0.65, 'min', 'subs'],
  ['TR', 'AI translation', 0.00025, 'word', 'text'],
  ['CTX', 'Context awareness', 0.0001, 'word', 'text'],
  ['TRREV', 'AI translation review', 0.001, 'word', 'text'],
  ['AEXP', 'Timing adaptation', 0.28, 'min', 'audio'],
  ['AEXPS', 'Timing adaptation', 0.07, 'min', 'audio'],
  ['CE', 'AI copy editing', 0.00025, 'word', 'text'],
  ['LUMO', 'Script adaptation', 0.98, 'film min', 'script'],
  ['TTSEL', 'Cloned voice', 0.165, 'min', 'voice'],
  ['TTSAZ', 'Neural voice', 0.10, 'min', 'voice'],
  ['TTSOA', 'Prompt-Driven voice', 0.10, 'min', 'voice'],
  ['VSWAP', 'Voice Changer', 0.40, 'min', 'voice'],
  ['STEM', 'Background separation', 0.15, 'min', 'audio'],
  ['V3', 'Expressive delivery', 0.08, 'min', 'voice'],
  ['LIP2', 'AI lip-sync, Standard', 3.95, 'min', 'lips'],
  ['LIPPREM', 'AI lip-sync, Premium', 6.60, 'min', 'lips'],
  ['LIPSTUDIO', 'AI lip-sync, Studio', 10.50, 'min', 'lips'],
  ['REVTTS', 'Re-voice a line', 0.021, 'change', 'other'],
  ['REVTXT', 'Split, join, shorten or lengthen a line', 0.03, 'change', 'other'],
  ['REVRETR', 'Retranslate a line', 0.0165, 'change', 'other'],
  ['REVSYNC', 'Auto-sync a line', 0.051, 'change', 'other'],
  ['REVLUMO', 'Retry a line’s adaptation', 0.09, 'change', 'other'],
  ['REVSTT', 'Re-transcribe a line', 0.0015, 'change', 'other'],
  ['REVSUB', 'Edit a subtitle', 0, 'change', 'other'],
];

export type PriceSampleOrg = 'lighthouse' | 'onehope';

/** The design's two example organizations; credit amounts match Plans & billing's normal and out situations. */
const ORGS: Record<PriceSampleOrg, { name: string; pct: number; admin: string; approvalOver: number; credit: { enough: number; short: number } }> = {
  lighthouse: { name: 'Lighthouse Media', pct: 0, admin: 'Dan Samper', approvalOver: 25, credit: { enough: 144.6, short: 1.63 } },
  onehope: { name: 'OneHope', pct: 40, admin: 'Ana Rivera', approvalOver: 200, credit: { enough: 2425.55, short: 113.79 } },
};

export interface PriceSample {
  org: PriceSampleOrg;
  short: boolean;
  member: boolean;
}

/** The sample chosen on the create form's address. */
export function pickPriceSample(url: string): PriceSample {
  const q = new URLSearchParams(url.split('?')[1]?.split('#')[0] ?? '');
  return {
    org: q.get('sampleOrg') === 'onehope' ? 'onehope' : 'lighthouse',
    short: q.get('sample') === 'out',
    member: q.get('sampleRole') === 'member',
  };
}

export function samplePriceCatalogue(): BillingCatalogue {
  return {
    version: 'sample · Per task pricing v3',
    currency: 'USD',
    rates: RATES.map(([key, label, price, unit, group]) => ({ key, label, price, unit, group })),
    groups: [],
    plans: [],
    packs: [],
    autoTopupSteps: [],
    approvalSteps: [],
    cloneHosting: 0,
    cloneSetup: 0,
    rollDays: 0,
    basicDubbing: { rates: [], translationRate: 'TR', wordsPerMinute: 150 },
    wordsPerPage: 250,
  };
}

/** The example organization's account; `viewerId` is put in its team so the viewer's role is the sample's. */
export function samplePriceAccount(orgId: string, viewerId: string, s: PriceSample): BillingAccount {
  const o = ORGS[s.org];
  const credit = s.short ? o.credit.short : o.credit.enough;
  const today = new Date().toISOString().slice(0, 10);
  return {
    asOf: today,
    organization: { id: orgId, name: o.name, country: '' },
    team: [
      { id: 'sample-admin', name: o.admin, initials: o.admin.split(' ').map(w => w[0]).join(''), role: 'admin' },
      { id: viewerId, name: 'You', initials: 'Y', role: s.member ? 'member' : 'admin' },
    ],
    subscription: { planId: s.org === 'onehope' ? 'enterprise' : 'team', since: today, lastChange: null, cycleStart: today, cycleEnd: today, renews: today, endsOn: null, pendingChange: null },
    agreement: null,
    discount: o.pct ? { pct: o.pct, label: o.name + ' discount', from: today, until: today, covers: 'every price in the price list' } : null,
    credit: { planCredit: credit, planUsed: 0, planLeft: credit, boughtLeft: 0, available: credit, used: 0, usedAtList: 0 },
    waiting: [],
    autoTopup: { bought: 0, spent: 0 },
    card: null,
    storage: { usedGB: 0, includedGB: 0 },
  };
}

export function samplePriceLimits(s: PriceSample): BillingLimits {
  return {
    atZero: 'pause',
    autoTopup: { on: false, below: 0, step: 0, cap: null },
    approvalOver: ORGS[s.org].approvalOver,
    approvalSteps: [],
    whoCanBuy: 'admins',
    alerts: { credit80: false, credit100: false, budgets: false, weekly: false },
  };
}
